import React, { useCallback, useEffect, useState } from 'react';
import {
  IoBanOutline,
  IoNotificationsOutline,
  IoPauseOutline,
  IoPencilOutline,
} from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import PagePanel from 'src/components/PagePanel';
import PageHeader from 'src/components/PageHeader';
import { Table, type TableSort } from 'src/components/TableNew';
import { Button } from 'src/components/Button';
import NoResult from 'src/components/NoResult/NoResult';
import { PermissionGuard } from 'src/components/PermissionGuard';
import { ConfirmModal } from 'src/components/ConfirmModal';
import { SendNotificationModal, type NotificationTarget } from 'src/components/SendNotificationModal';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { fetchUsers, updateFilter, resetFilters } from 'src/store/slice/users';
import {
  KYC_STATUS_OPTIONS,
  USERS_HEADERS,
  USER_STATUS_OPTIONS,
} from 'src/utils/constants';

// One bar per filter; the API ANDs them (`?email=&country_code=&status=`).
// Status and KYC status are exact-match on the API, so they get pickers.
const USER_FILTERS: FilterDef[] = [
  { key: 'name', label: 'Name', placeholder: 'First or "first last"' },
  { key: 'email', label: 'Email', placeholder: 'Starts with…' },
  { key: 'bxlend_id', label: 'BxLend ID', placeholder: 'Starts with…' },
  { key: 'country_code', label: 'Country', placeholder: 'e.g. PK' },
  { key: 'status', label: 'Status', options: USER_STATUS_OPTIONS, allLabel: 'All statuses' },
  { key: 'kyc_status', label: 'KYC status', options: KYC_STATUS_OPTIONS, allLabel: 'All KYC statuses' },
];
import { ROUTE_USERS } from 'src/utils/routes';
import { PERMISSIONS } from 'src/utils/permissions';
import { convertDate } from 'src/components/Table/helpers';
import {
  AdminPlatformUser,
  formatKycStatusLabel,
  formatUserDisplayName,
  formatUserStatusLabel,
  updatePlatformUserStatus,
} from 'src/api/adminUsers';

import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';

import UserStatusModal from './components/UserStatusModal';
import { ActionIconButton, ActionsRow, NameLink, StatusText } from './styled';

const formatTableName = (user: AdminPlatformUser) => {
  const first = user.name?.first?.trim() || '';
  const last = user.name?.last?.trim() || '';
  return `${first} ${last}`.trim() || '—';
};

const statusTone = (raw?: string): 'positive' | 'negative' | 'muted' | 'warning' => {
  const value = String(raw || '')
    .toUpperCase()
    .replace(/_/g, ' ');
  if (['ACTIVE', 'VERIFIED', 'SUCCESS', 'COMPLETED'].includes(value)) return 'positive';
  if (['BANNED', 'SUSPENDED', 'SUSPICIOUS', 'CANCELED', 'CANCELLED', 'FAILED'].includes(value)) {
    return 'negative';
  }
  if (['PENDING', 'PROCESSING'].includes(value)) return 'warning';
  return 'muted';
};

type ConfirmAction = {
  user: AdminPlatformUser;
  kind: 'suspend' | 'ban';
};

// Columns the API can order by, mapped from the table's field name.
const SORT_FIELDS: Record<string, string> = {
  display_name: 'name',
  email: 'email',
  status: 'status',
  kyc_status: 'kyc_status',
  created_at: 'created_at',
};

const UsersList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [statusTarget, setStatusTarget] = useState<AdminPlatformUser | null>(null);
  const [sort, setSort] = useState<TableSort>(null);
  const [notifyTarget, setNotifyTarget] = useState<NotificationTarget | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    data: { users, totalCount, totalPages, filters },
    loading,
  } = useSelector(({ users }) => users);

  const runQuery = useCallback(
    (params: { page: number; filters: Record<string, string> }) => {
      dispatch(
        fetchUsers({
          page: params.page,
          filters: params.filters,
          // The table's display column maps onto the API's sort key.
          sort: sort ? SORT_FIELDS[sort.field] : undefined,
          order: sort?.direction,
        }),
      );
    },
    // `sort` belongs to the query, so a change re-runs it from page 1.
    [dispatch, sort],
  );

  const { applied: appliedFilters, refresh: refreshQuery } = useFilterQueryEffect({
    filters,
    currentPage,
    setCurrentPage,
    query: runQuery,
  });

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  const notifyComingSoon = (feature: string) => {
    dispatch(
      setAppAlert({
        message: `${feature} is not available yet — API pending.`,
        isSuccess: false,
      }),
    );
  };

  // The table's display string is kept under its own key: writing it over
  // `name` shadowed the API's { first, last } object for every consumer of the
  // row, the edit modal included.
  const rows = (users as AdminPlatformUser[]).map((user) => ({
    ...user,
    id: user.id,
    display_name: formatTableName(user),
    email: user.email,
    status: user.status,
    kyc_status: user.kyc_status,
    created_at: user.created_at,
  }));

  const refreshList = () => {
    dispatch(fetchUsers({ page: currentPage, filters: appliedFilters }));
  };

  return (
    <PagePanel>
      <PageHeader
        title={`Users (${totalCount})`}
        hideSearch
        headerActions={
          <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
            {/* <Button
              text="Notify all"
              className="outline"
              onClick={() => setNotifyTarget({ kind: 'all' })}
            /> */}
            <Button
              text="Add"
              className="black"
              onClick={() => notifyComingSoon('Add user')}
            />
          </PermissionGuard>
        }
      >
        <FilterBar
          filters={USER_FILTERS}
          values={filters}
          onChange={(key, value) => dispatch(updateFilter({ key, value }))}
          onClear={() => dispatch(resetFilters())}
          onRefresh={refreshQuery}
        />
      </PageHeader>

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={USERS_HEADERS}
          items={rows}
          columns="1.2fr 1.4fr 0.9fr 1fr 1fr"
          fieldsToShow={['display_name', 'email', 'status', 'kyc_status', 'created_at']}
          sortableFields={Object.keys(SORT_FIELDS)}
          sort={sort}
          onSortChange={setSort}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          actionsHeader="Quick Actions"
          onRowClick={(item) => navigate(`${ROUTE_USERS}/${item.id}`)}
          renderCell={(item, field) => {
            if (field === 'display_name') {
              const empty = !item.display_name || item.display_name === '—';
              return (
                <NameLink
                  type="button"
                  $muted={empty}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${ROUTE_USERS}/${item.id}`);
                  }}
                >
                  {item.display_name || '—'}
                </NameLink>
              );
            }
            if (field === 'email') {
              return <span>{item.email || '—'}</span>;
            }
            if (field === 'status') {
              return (
                <StatusText $tone={statusTone(item.status)}>
                  {formatUserStatusLabel(item.status)}
                </StatusText>
              );
            }
            if (field === 'kyc_status') {
              return (
                <StatusText $tone={statusTone(item.kyc_status)}>
                  {formatKycStatusLabel(item.kyc_status)}
                </StatusText>
              );
            }
            if (field === 'created_at') {
              if (!item.created_at) return <span>—</span>;
              const { date } = convertDate(item.created_at);
              return <span>{date}</span>;
            }
            return null;
          }}
          actions={(item) => {
            const user = item as AdminPlatformUser;
            const status = String(user.status || '').toUpperCase();
            const isSuspended = status === 'SUSPENDED';
            const isBanned = status === 'BANNED';
            const pauseDisabled = isSuspended || isBanned;
            const banDisabled = isBanned;

            return (
              <PermissionGuard permission={PERMISSIONS.USER_WRITE}>
                <ActionsRow>
                  <ActionIconButton
                    type="button"
                    aria-label="Edit user"
                    title="Edit user"
                    onClick={() => setStatusTarget(user)}
                  >
                    <IoPencilOutline size={16} />
                  </ActionIconButton>
                  <ActionIconButton
                    type="button"
                    aria-label="Mark as Suspicious"
                    title={
                      isBanned
                        ? 'User is banned'
                        : isSuspended
                          ? 'Already Suspicious'
                          : 'Mark as Suspicious'
                    }
                    disabled={pauseDisabled}
                    onClick={() => setConfirmAction({ user, kind: 'suspend' })}
                  >
                    <IoPauseOutline size={16} />
                  </ActionIconButton>
                  <ActionIconButton
                    type="button"
                    aria-label="Ban user"
                    title={isBanned ? 'Already banned' : 'Ban user'}
                    disabled={banDisabled}
                    onClick={() => setConfirmAction({ user, kind: 'ban' })}
                  >
                    <IoBanOutline size={16} />
                  </ActionIconButton>
                  <ActionIconButton
                    type="button"
                    aria-label="Send notification"
                    title="Send notification"
                    onClick={() =>
                      setNotifyTarget({
                        kind: 'individual',
                        userId: String(user.id),
                        userLabel: formatUserDisplayName(user),
                      })
                    }
                  >
                    <IoNotificationsOutline size={16} />
                  </ActionIconButton>
                </ActionsRow>
              </PermissionGuard>
            );
          }}
        />
      ) : (
        <NoResult />
      )}

      {notifyTarget && (
        <SendNotificationModal
          target={notifyTarget}
          onClose={() => setNotifyTarget(null)}
        />
      )}

      {statusTarget && (
        <UserStatusModal
          user={statusTarget}
          onClose={() => setStatusTarget(null)}
          onUpdated={() => {
            setStatusTarget(null);
            refreshList();
          }}
        />
      )}

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.kind === 'ban' ? 'Ban user' : 'Mark as Suspicious'}
          message={
            confirmAction.kind === 'ban' ? (
              <>
                Ban <strong>{formatUserDisplayName(confirmAction.user)}</strong>? This will set
                status to BANNED when confirmed.
              </>
            ) : (
              <>
                Mark <strong>{formatUserDisplayName(confirmAction.user)}</strong> as Suspicious?
                This will set status to SUSPENDED.
              </>
            )
          }
          confirmText={confirmAction.kind === 'ban' ? 'Ban' : 'Confirm'}
          confirmClassName="negative"
          onClose={() => setConfirmAction(null)}
          onConfirm={async () => {
            const nextStatus = confirmAction.kind === 'ban' ? 'BANNED' : 'SUSPENDED';
            try {
              await updatePlatformUserStatus(confirmAction.user.id, nextStatus);
              dispatch(
                setAppAlert({
                  message:
                    confirmAction.kind === 'ban' ? 'User banned' : 'User marked as Suspicious',
                  isSuccess: true,
                }),
              );
              setConfirmAction(null);
              refreshList();
            } catch (e: any) {
              dispatch(
                setAppAlert({
                  message: e?.response?.data?.error ?? 'Failed to update user status',
                  isSuccess: false,
                }),
              );
            }
          }}
        />
      )}
    </PagePanel>
  );
};

export default UsersList;
